import React, { useCallback, useMemo, useEffect } from 'react';
import { CustomSelect } from 'apps/sigma2-ui/src/components/ui/CustomSelect';
import { CustomSelectOption } from 'apps/sigma2-ui/src/components/ui/CustomSelect';
import { TableViewFilterProps } from 'libs/end-to-end/src/custom-viz/TableViewTypes';
import { useGetColumnDisplayName } from 'apps/sigma2-ui/src/explore-section/hooks/useGetColumnDisplayName';
import { useMetricConfig } from 'apps/sigma2-ui/src/lib/hooks/useMetricConfig';

// eslint-disable-next-line complexity
export const TableViewMetricFilter = (props: TableViewFilterProps) => {
  const { data, setViewConfiguration, viewConfiguration } = props;

  const { getColumnDisplayName } = useGetColumnDisplayName();

  const { getMetricAndFocusAreaFromColumnName } = useMetricConfig();

  const getIsMetricBoolean = useCallback(
    (columnName: string) => {
      if (!viewConfiguration.selectedMetric) {
        return undefined;
      }

      const metricInfo = getMetricAndFocusAreaFromColumnName({
        columnName,
      });

      return metricInfo?.metric.isMetricBoolean;
    },
    [getMetricAndFocusAreaFromColumnName, viewConfiguration.selectedMetric],
  );

  const metricOptions: CustomSelectOption[] = useMemo(() => {
    return data.columnHeaders.map((columnName) => ({
      value: columnName,
      label: getColumnDisplayName(columnName),
      disabled:
        viewConfiguration.overallDisplayMode === 'count' &&
        getIsMetricBoolean(columnName) === false,
    }));
  }, [
    data.columnHeaders,
    getColumnDisplayName,
    getIsMetricBoolean,
    viewConfiguration.overallDisplayMode,
  ]);

  const metricChangeCb = useCallback(
    (metric: string) => {
      if (setViewConfiguration && viewConfiguration) {
        setViewConfiguration({
          ...viewConfiguration,
          selectedMetric: metric,
        });
      }
    },
    [setViewConfiguration, viewConfiguration],
  );

  useEffect(() => {
    if (viewConfiguration.selectedMetric || metricOptions.length === 0) {
      return;
    }

    metricChangeCb(metricOptions[0]?.value);
  }, [viewConfiguration.selectedMetric, metricOptions, metricChangeCb]);

  if (metricOptions.length === 0) {
    return null;
  }

  return (
    <div className="flex items-center gap-2">
      <CustomSelect
        selectedValue={
          viewConfiguration.selectedMetric || metricOptions[0]?.value || ''
        }
        options={metricOptions}
        onChange={metricChangeCb}
      />
    </div>
  );
};
